/**
 * What a chat thread is called on screen.
 *
 * A project's main thread is written once, at creation, with the project's
 * name copied into both `title` and `projectName` — and neither copy is ever
 * updated. So a renamed project keeps announcing its old name, and the first
 * projects ever created in the cloud environment carry a placeholder that was
 * never their name at all. The stored copies are left alone; the name is read
 * from the live project instead, every time it is shown.
 *
 * Group threads are the opposite case: their title is one a user chose, and it
 * is meant to differ from the project's name. Only the main thread borrows.
 *
 * Every function here takes the thread's *own* project — the one whose id is
 * `thread.projectId`, not whichever project happens to be open. Pass nothing
 * when it isn't loaded and the stored copy stands in, as it does today.
 */

/** The project's own chat: not a group, not a 1:1. */
export const isMainProjectThread = (thread) =>
  Boolean(thread) &&
  !thread.isSubthread &&
  !thread.isPrivate &&
  !thread.directRecipient;

/** The name of the project the thread belongs to. */
export const getThreadProjectName = (thread, project) =>
  project?.name || thread?.projectName || '';

/** The name of the thread itself. */
export const getThreadTitle = (thread, project) =>
  isMainProjectThread(thread)
    ? getThreadProjectName(thread, project) || thread.title || ''
    : thread?.title || '';

/**
 * How a thread is announced above a dialog: which project, then which
 * conversation inside it. The main thread *is* the project's conversation, so
 * it is the project's name alone — the two halves would otherwise be the same
 * word twice.
 */
export const getThreadHeading = (thread, project) => {
  const title = getThreadTitle(thread, project);
  if (isMainProjectThread(thread)) {
    return title;
  }
  const projectName = getThreadProjectName(thread, project);
  if (!projectName) return title;
  if (!title) return projectName;
  return `${projectName} - ${title}`;
};
